import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { getStoredUserEmail } from "../../../../utils/localStorage.js";
import { apiUrl } from "../../../../utils/api.js";
import { getPlatformEmailPattern } from "../../../../config/platformEmailPattern.js";
import { OAUTH_ERROR_MESSAGES, LOGIN_EMAIL_REGEX } from "./constants.js";
import { computeMatchedCustomerDataForEmail } from "./computeLoginMatch.js";
import { getEnvEmailDomainConfigs, resolveCustomerEnv } from "./emailAndEnvUtils.js";
import { normalizeAuthErrorMessage } from "./errors.js";
import {
  buildReturnUrlWithCode,
  cacheOriginalHostnameMarker,
  parseHttpUrlOrNull,
} from "./returnUrl.js";
import { isMainCustomerPortalShell } from "../../../../utils/customerPortalDocument.js";
import { fetchAuthStatus, fetchReturnCodeForCaller } from "./authApi.js";
import { resolveSlugForAuthLogin } from "./slug.js";
import {
  formatIdpLoginButtonLabel,
  resolveSignInOptionsForEmail,
  signInOptionUsesLocalPassword,
} from "./signInOptions.js";

/** So API routes that read `x-original-host` (odd1-XXXXX) still see the portal host when `apiUrl` points at localhost. */
function xOriginalHostHeaders() {
  if (typeof window === "undefined" || !window.location?.host) return {};
  return { "X-Original-Host": window.location.host };
}

function computeInitialReturnUrl() {
  if (typeof window === "undefined") return null;
  const urlParams = new URLSearchParams(window.location.search);
  const returnUrlParam = urlParams.get("return_url");
  if (returnUrlParam) return decodeURIComponent(returnUrlParam);
  const path = window.location.pathname || "";
  const isOnboardingRoute = /\/onboard\/?$/.test(path);
  const isAcceptInviteRoute = /^\/accept-invite\/?$/i.test((path || "").replace(/\/+$/, "") || "");
  if (
    isMainCustomerPortalShell({
      hostname: window.location.hostname,
      pathname: path,
      isOnboardingRoute,
      isAcceptInviteRoute,
    })
  ) {
    return `${window.location.origin}/`;
  }
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}`;
}

function platformOktaFromWindow() {
  return typeof window !== "undefined" && String(window.ENV?.IDENTITY_PROVIDER_TYPE || "").toLowerCase() === "okta";
}

/** True when we should show the Okta silent-connect splash instead of the full login form (first paint). */
function shouldProbeAdminOktaSilent(isAdminAccess) {
  if (!isAdminAccess || !platformOktaFromWindow() || typeof window === "undefined") return false;
  const qs = new URLSearchParams(window.location.search);
  if (qs.get("error")) return false;
  if (qs.get("force_reauth") === "true") return false;
  if (sessionStorage.getItem("force_reauth") === "true") return false;
  if (sessionStorage.getItem("admin_okta_silent_attempted") === "1") return false;
  return true;
}

export function useSSOLoginCard({ brand, isAdminAccess = false, customerKey, onLogin }) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedCustomerData, setFetchedCustomerData] = useState(null);
  const [matchedCustomerData, setMatchedCustomerData] = useState(null);
  const [localPassword, setLocalPassword] = useState("");
  const [showLocalPassword, setShowLocalPassword] = useState(false);
  const [returnUrl, setReturnUrl] = useState(() => computeInitialReturnUrl());
  const [adminSilentProbeActive, setAdminSilentProbeActive] = useState(() => shouldProbeAdminOktaSilent(isAdminAccess));
  const [forgotStep, setForgotStep] = useState(null);
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedIdpConfig, setSelectedIdpConfig] = useState(null);
  const initialEmailRef = useRef("");
  const didAutoReturnRef = useRef(false);
  const lastBlurredResolvedEmailRef = useRef(null);
  const platformUseOkta = platformOktaFromWindow();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const path = window.location.pathname || "";
    const isOnboardingRoute = /\/onboard\/?$/.test(path);
    const isAcceptInviteRoute = /^\/accept-invite\/?$/i.test(
      (path || "").replace(/\/+$/, "") || ""
    );

    const returnUrlParam = urlParams.get("return_url");
    if (returnUrlParam) {
      setReturnUrl(decodeURIComponent(returnUrlParam));
    } else if (
      isMainCustomerPortalShell({
        hostname: window.location.hostname,
        pathname: path,
        isOnboardingRoute,
        isAcceptInviteRoute,
      })
    ) {
      setReturnUrl(`${window.location.origin}/`);
    } else {
      const { origin, pathname, search } = window.location;
      setReturnUrl(`${origin}${pathname}${search}`);
    }

    const oauthError = urlParams.get("error");
    if (oauthError) {
      setAdminSilentProbeActive(false);
      // Expected when silent Okta sign-in is not possible — not a user-facing error.
      if (oauthError === "login_required") {
        setError(null);
      } else {
        let message = OAUTH_ERROR_MESSAGES[oauthError] || `Sign-in failed: ${oauthError}. Please try again.`;
        if (oauthError === "token_exchange_failed" || oauthError === "admin_token_failed") {
          const callbackExample = `${window.location.origin}/auth/callback`;
          message = `Sign-in failed because the identity server is unreachable or misconfigured. Verify issuer/hosted domain, client credentials, and this exact callback URL: ${callbackExample}`;
        }
        setError(message);
      }
      const cleanSearch = new URLSearchParams(window.location.search);
      cleanSearch.delete("error");
      const newSearch = cleanSearch.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
      window.history.replaceState(null, null, newUrl);
    }

    const fetchCustomerData = async () => {
      try {
        if (isAdminAccess) {
          return;
        }

        const slugParam = customerKey ? `?slug=${encodeURIComponent(customerKey)}` : "";
        const response = await fetch(apiUrl(`/api/v1/customers${slugParam}`), {
          headers: {
            ...xOriginalHostHeaders(),
          },
          credentials: "include",
        });
        if (response.ok) {
          const customer = await response.json();
          setFetchedCustomerData(customer);
          if (customerKey && customer?.slug && String(customer.slug).toLowerCase() === String(customerKey).toLowerCase()) {
            setMatchedCustomerData({ ...customer, matchSource: "url" });
          }
        }
      } catch {
        // ignore
      }
    };

    fetchCustomerData();
  }, [customerKey, isAdminAccess]);

  /**
   * Deadbolt / super-admin + Okta: if the user already has an Okta session (e.g. they launched this app from the Okta
   * dashboard), attempt OIDC silent auth once so they are not forced to type email first.
   */
  useEffect(() => {
    if (!isAdminAccess || !platformUseOkta) {
      setAdminSilentProbeActive(false);
      return;
    }
    if (!returnUrl) {
      setAdminSilentProbeActive(false);
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("force_reauth") === "true" || sessionStorage.getItem("force_reauth") === "true") {
      setAdminSilentProbeActive(false);
      return;
    }

    let cancelled = false;
    const run = async () => {
      if (sessionStorage.getItem("admin_okta_silent_attempted") === "1") {
        setAdminSilentProbeActive(false);
        return;
      }
      const status = await fetchAuthStatus();
      if (cancelled) {
        setAdminSilentProbeActive(false);
        return;
      }
      if (status.authenticated) {
        setAdminSilentProbeActive(false);
        return;
      }

      const pathname = window.location.pathname || "";
      const isPlatformRoot =
        pathname === "" || pathname === "/" || /^\/staging\/?$/.test(pathname) || pathname.includes("/admin");
      const adminReturnUrl = `${window.location.origin}/`;
      const sameOriginReturn =
        returnUrl && returnUrl.startsWith(window.location.origin + "/") && !returnUrl.includes("/customer");
      const crossOriginReturn = returnUrl && parseHttpUrlOrNull(returnUrl) && !returnUrl.includes("/customer");
      const finalReturnTo =
        isAdminAccess || (isPlatformRoot && !returnUrl?.includes("/customer"))
          ? (sameOriginReturn ? returnUrl : crossOriginReturn ? returnUrl : adminReturnUrl)
          : returnUrl;

      try {
        const params = new URLSearchParams({
          admin: "true",
          silent_sso: "true",
          return_to: finalReturnTo,
        });
        const hint = getStoredUserEmail();
        if (hint && LOGIN_EMAIL_REGEX.test(String(hint).trim())) {
          params.set("email", String(hint).trim());
        }
        const response = await fetch(apiUrl(`/api/v1/auth/authorization_url?${params.toString()}`), {
          credentials: "include",
          headers: { ...xOriginalHostHeaders() },
        });
        if (!response.ok) {
          setAdminSilentProbeActive(false);
          return;
        }
        const data = await response.json();
        if (cancelled) {
          setAdminSilentProbeActive(false);
          return;
        }
        if (!data?.authorization_url) {
          setAdminSilentProbeActive(false);
          return;
        }
        sessionStorage.setItem("admin_okta_silent_attempted", "1");
        window.location.href = data.authorization_url;
      } catch {
        setAdminSilentProbeActive(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [isAdminAccess, platformUseOkta, returnUrl]);

  useEffect(() => {
    const run = async () => {
      if (didAutoReturnRef.current) return;
      if (!returnUrl) return;
      try {
        if (new URL(returnUrl, window.location.origin).origin === window.location.origin) return;
      } catch {
        /* ignore invalid return_url */
      }

      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.get("return_url")) return;
      if (sessionStorage.getItem("force_reauth") === "true" || urlParams.get("force_reauth") === "true") return;
      if (!parseHttpUrlOrNull(returnUrl)) return;

      setIsLoading(true);
      const status = await fetchAuthStatus();
      if (!status.ok || !status.authenticated) {
        setIsLoading(false);
        return;
      }

      const slug = resolveSlugForAuthLogin({ matchedCustomerData, fetchedCustomerData, customerKey });
      const codePayload = await fetchReturnCodeForCaller({ slug });
      if (!codePayload) {
        setIsLoading(false);
        setError("Authenticated session detected, but failed to get a return code for the calling app.");
        return;
      }

      const finalUrl = buildReturnUrlWithCode(returnUrl, codePayload);
      if (!finalUrl) {
        setIsLoading(false);
        setError("Could not return to the calling app. Invalid return_url or missing code.");
        return;
      }

      didAutoReturnRef.current = true;
      window.location.replace(finalUrl);
    };

    run();
  }, [returnUrl, matchedCustomerData, fetchedCustomerData, customerKey]);

  const updateMatchedCustomerDataForEmail = useCallback(
    (emailValue) => {
      setMatchedCustomerData(
        computeMatchedCustomerDataForEmail(emailValue, { isAdminAccess, customerKey, fetchedCustomerData })
      );
    },
    [isAdminAccess, customerKey, fetchedCustomerData]
  );

  // Keep login match in sync whenever the email field changes (valid or not).
  // Blur-only updates left the password / SSO chooser hidden until the user left the field.
  useEffect(() => {
    updateMatchedCustomerDataForEmail(email);
  }, [email, updateMatchedCustomerDataForEmail]);

  const commitLoginIdentityFromEmailField = useCallback(() => {
    const trimmed = String(email || "").trim();
    const valid = trimmed && LOGIN_EMAIL_REGEX.test(trimmed);
    const next = computeMatchedCustomerDataForEmail(email, { isAdminAccess, customerKey, fetchedCustomerData });
    flushSync(() => {
      setMatchedCustomerData(next);
    });
    lastBlurredResolvedEmailRef.current = valid ? trimmed.toLowerCase() : null;
    return next;
  }, [email, isAdminAccess, customerKey, fetchedCustomerData]);

  const validateLoginEmail = useCallback(() => {
    const trimmedEmail = email.trim();
    const normalizedEmail = trimmedEmail.toLowerCase();
    if (!trimmedEmail) {
      setEmailError("Please enter your email address");
      return null;
    }

    if (!LOGIN_EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("Please enter a valid email address");
      return null;
    }

    setEmailError("");
    return { trimmedEmail, normalizedEmail };
  }, [email]);

  const cacheLocalLoginPayload = useCallback(
    (data) => {
      const token = String(data?.token || data?.auth_token || "").trim();
      const expiresIn = Number(data?.expires_in || 3600);
      if (token) {
        localStorage.setItem("auth_token", token);
        localStorage.setItem("token_expires", String(Date.now() + (expiresIn * 1000)));
      }
      const emailFromPayload = String(data?.user?.email || email || "").trim();
      const nameFromPayload = String(data?.user?.name || "").trim();
      if (emailFromPayload) localStorage.setItem("user_email", emailFromPayload);
      if (nameFromPayload) localStorage.setItem("user_name", nameFromPayload);
    },
    [email]
  );

  const startSsoLoginFlow = async (loginMatch) => {
    const validatedEmail = validateLoginEmail();
    if (!validatedEmail) return false;
    const { trimmedEmail, normalizedEmail } = validatedEmail;
    const forceReauthRequested =
      sessionStorage.getItem("force_reauth") === "true"
      || new URLSearchParams(window.location.search).get("force_reauth") === "true";

    setPasswordError("");
    setIsLoading(true);

    const currentUserEmail = getStoredUserEmail();
    const switchingUsers = currentUserEmail && String(currentUserEmail).trim().toLowerCase() !== normalizedEmail;

    if (switchingUsers) {
      sessionStorage.setItem("force_reauth", "true");
    }

    try {
      const usePlatformAdminFlow = isAdminAccess || loginMatch?.type === "internal";
      if (!usePlatformAdminFlow) {
        setIsLoading(false);
        return false;
      }
      if (usePlatformAdminFlow && loginMatch) {
        if (platformUseOkta) {
          cacheOriginalHostnameMarker();
          const params = new URLSearchParams({
            admin: "true",
            email: trimmedEmail,
          });
          const pathname = window.location.pathname || "";
          const isPlatformRoot =
            pathname === "" || pathname === "/" || /^\/staging\/?$/.test(pathname) || pathname.includes("/admin");
          const adminReturnUrl = `${window.location.origin}/`;
          const sameOriginReturn = returnUrl && returnUrl.startsWith(window.location.origin + "/") && !returnUrl.includes("/customer");
          const crossOriginReturn = returnUrl && parseHttpUrlOrNull(returnUrl) && !returnUrl.includes("/customer");
          const finalReturnTo =
            isAdminAccess || (isPlatformRoot && !returnUrl?.includes("/customer"))
              ? (sameOriginReturn ? returnUrl : crossOriginReturn ? returnUrl : adminReturnUrl)
              : returnUrl;
          params.set("return_to", finalReturnTo);
          if (switchingUsers || forceReauthRequested) {
            params.set("force_reauth", "true");
          }
          const response = await fetch(apiUrl(`/api/v1/auth/authorization_url?${params.toString()}`), {
            credentials: "include",
            headers: { ...xOriginalHostHeaders() },
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(normalizeAuthErrorMessage(errorData.error || "Failed to get authorization URL"));
          }
          const data = await response.json();
          window.location.href = data.authorization_url;
          return true;
        }
        const pattern = getPlatformEmailPattern();
        const patternDisplay = pattern && pattern.trim() ? pattern.replace("@", "") : "configured domain";
        setEmailError(`Email domain not recognized. Only ${patternDisplay} emails are allowed for admin access.`);
        setIsLoading(false);
        return false;
      }
      if (usePlatformAdminFlow && !loginMatch) {
        const pattern = getPlatformEmailPattern();
        const patternDisplay = pattern && pattern.trim() ? pattern.replace("@", "") : "configured domain";
        setEmailError(`Email domain not recognized. Only ${patternDisplay} emails are allowed for admin access.`);
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      setError(normalizeAuthErrorMessage(err?.message || "Failed to initiate login. Please try again."));
      setIsLoading(false);
      return false;
    }
  };

  const startCustomerLoginFlow = async (loginMatch) => {
    const validatedEmail = validateLoginEmail();
    if (!validatedEmail) return false;
    const { trimmedEmail } = validatedEmail;

    if (!localPassword.trim()) {
      setPasswordError("Please enter your password");
      return false;
    }
    setPasswordError("");
    setError(null);
    setIsLoading(true);

    try {
      if (!loginMatch?.slug) {
        setEmailError("Email domain not recognized. Please contact your administrator.");
        setIsLoading(false);
        return false;
      }
      const customerEnv = resolveCustomerEnv(fetchedCustomerData || loginMatch);
      const body = {
        email: trimmedEmail,
        password: localPassword,
        slug: loginMatch.slug,
        env: customerEnv,
        path: window.location.pathname || "/",
      };
      if (returnUrl && parseHttpUrlOrNull(returnUrl)) body.return_url = returnUrl;
      const response = await fetch(apiUrl("/api/v1/auth/customer_login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.error || "Failed to sign in";
        setError(message);
        setIsLoading(false);
        return false;
      }

      cacheLocalLoginPayload(data);

      if (data?.redirect_url) {
        window.location.replace(String(data.redirect_url));
        return true;
      }

      let validReturnUrl = returnUrl;
      if (validReturnUrl) {
        try {
          const returnOrigin = new URL(validReturnUrl, window.location.origin).origin;
          if (returnOrigin === window.location.origin) {
            validReturnUrl = null;
          }
        } catch {
          validReturnUrl = null;
        }
      }
      if (validReturnUrl) {
        const codePayload = await fetchReturnCodeForCaller({ slug: data?.user?.customer ?? loginMatch?.slug });
        const finalUrl = codePayload ? buildReturnUrlWithCode(validReturnUrl, codePayload) : null;
        if (finalUrl) {
          window.location.replace(finalUrl);
          return true;
        }
      }

      const currentPathSegment = (window.location.pathname || "").replace(/^\/+|\/+$/g, "");
      if (currentPathSegment && !currentPathSegment.includes("/")) {
        window.location.replace(window.location.href);
        return true;
      }

      if (typeof onLogin === "function") {
        onLogin(data);
      }
      setIsLoading(false);
      return true;
    } catch (err) {
      setError(err?.message || "Failed to sign in.");
      setIsLoading(false);
      return false;
    }
  };

  const startCustomerSsoRedirect = async (idpConfig) => {
    const loginMatch = commitLoginIdentityFromEmailField();
    const validatedEmail = validateLoginEmail();
    if (!validatedEmail) return;
    const { trimmedEmail } = validatedEmail;
    if (!loginMatch?.slug || !idpConfig?.provider_name) return;
    setError(null);
    setIsLoading(true);
    try {
      const customerEnv = resolveCustomerEnv(fetchedCustomerData || loginMatch);
      const params = new URLSearchParams({
        slug: loginMatch.slug,
        env: customerEnv,
        email: trimmedEmail,
        identity_provider: idpConfig.provider_name,
      });
      if (returnUrl && parseHttpUrlOrNull(returnUrl)) params.set("return_to", returnUrl);
      const response = await fetch(apiUrl(`/api/v1/auth/authorization_url?${params.toString()}`), {
        credentials: "include",
        headers: { ...xOriginalHostHeaders() },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(normalizeAuthErrorMessage(data?.error || "Failed to get sign-in URL"));
        setIsLoading(false);
        return;
      }
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      setError("Failed to get sign-in URL.");
    } catch (err) {
      setError(normalizeAuthErrorMessage(err?.message || "Failed to initiate sign-in."));
    }
    setIsLoading(false);
  };

  const startPlatformLoginFlow = async () => {
    const validatedEmail = validateLoginEmail();
    if (!validatedEmail) return false;
    const { trimmedEmail } = validatedEmail;

    if (!localPassword.trim()) {
      setPasswordError("Please enter your password");
      return false;
    }
    setPasswordError("");
    setError(null);
    setIsLoading(true);

    try {
      const pathname = window.location.pathname || "";
      const isPlatformRoot =
        pathname === "" || pathname === "/" || /^\/staging\/?$/.test(pathname) || pathname.includes("/admin");
      const adminReturnUrl = `${window.location.origin}/`;
      const sameOriginReturn = returnUrl && returnUrl.startsWith(window.location.origin + "/") && !returnUrl.includes("/customer");
      const crossOriginReturn = returnUrl && parseHttpUrlOrNull(returnUrl) && !returnUrl.includes("/customer");
      const finalReturnTo =
        isAdminAccess || (isPlatformRoot && !returnUrl?.includes("/customer"))
          ? (sameOriginReturn ? returnUrl : crossOriginReturn ? returnUrl : adminReturnUrl)
          : returnUrl;

      const response = await fetch(apiUrl("/api/v1/auth/platform_login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmedEmail,
          password: localPassword,
          return_to: finalReturnTo,
        }),
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.error || "Failed to sign in";
        setError(message);
        setIsLoading(false);
        return false;
      }

      cacheLocalLoginPayload(data);

      if (data.redirect_url) {
        let redirectUrl = String(data.redirect_url).trim();
        if (data.token) {
          const sep = redirectUrl.includes("?") ? "&" : "?";
          redirectUrl = `${redirectUrl}${sep}token=${encodeURIComponent(data.token)}`;
        }
        window.location.replace(redirectUrl);
        return true;
      }
      if (typeof onLogin === "function") {
        onLogin(data);
      }
      setIsLoading(false);
      return true;
    } catch (err) {
      setError(err?.message || "Failed to sign in.");
      setIsLoading(false);
      return false;
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const loginMatch = commitLoginIdentityFromEmailField();
    const usePlatformAdminFlow = isAdminAccess || loginMatch?.type === "internal";
    if (usePlatformAdminFlow && loginMatch) {
      if (platformUseOkta) {
        await startSsoLoginFlow(loginMatch);
        return;
      }
      await startPlatformLoginFlow();
      return;
    }
    const optionsOnSubmit = resolveSignInOptionsForEmail(envConfigsForCurrentEnv, email);
    const soleFederated =
      optionsOnSubmit.length === 1 && !signInOptionUsesLocalPassword(optionsOnSubmit[0]);
    if (soleFederated) {
      await startCustomerSsoRedirect(optionsOnSubmit[0]);
      return;
    }
    await startCustomerLoginFlow(loginMatch);
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setForgotStep("request");
    setError(null);
  };

  const handleForgotBack = () => {
    setForgotStep(null);
    setForgotCode("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setShowForgotNewPassword(false);
    setError(null);
  };

  const handleForgotRequestSubmit = async (e) => {
    e.preventDefault();
    const loginMatch = commitLoginIdentityFromEmailField();
    const validatedEmail = validateLoginEmail();
    if (!validatedEmail) return;
    if (!loginMatch?.slug) {
      setEmailError("Email domain not recognized.");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const customerEnv = resolveCustomerEnv(fetchedCustomerData || loginMatch);
      const res = await fetch(apiUrl("/api/v1/auth/forgot_password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: validatedEmail.trimmedEmail,
          slug: loginMatch.slug,
          env: customerEnv,
        }),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || "Could not send reset instructions. Please try again.");
        setIsLoading(false);
        return;
      }
      setForgotStep("confirm");
      setError(null);
    } catch (err) {
      setError(err?.message || "Could not send reset instructions. Please try again.");
    }
    setIsLoading(false);
  };

  const handleForgotConfirmSubmit = async (e) => {
    e.preventDefault();
    const loginMatch = commitLoginIdentityFromEmailField();
    const validatedEmail = validateLoginEmail();
    if (!validatedEmail) return;
    if (!forgotCode.trim()) {
      setError("Enter the verification code from your email.");
      return;
    }
    if (!forgotNewPassword) {
      setError("Enter a new password.");
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (!loginMatch?.slug) {
      setError("Session context missing. Please go back and try again.");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const customerEnv = resolveCustomerEnv(fetchedCustomerData || loginMatch);
      const res = await fetch(apiUrl("/api/v1/auth/confirm_forgot_password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: validatedEmail.trimmedEmail,
          code: forgotCode.trim(),
          new_password: forgotNewPassword,
          slug: loginMatch.slug,
          env: customerEnv,
        }),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || "Could not reset password. Please try again.");
        setIsLoading(false);
        return;
      }
      handleForgotBack();
      setError(null);
      setEmailError("");
      setPasswordError("");
      setLocalPassword("");
      setSuccessMessage("Your password has been reset. You can sign in with your new password.");
    } catch (err) {
      setError(err?.message || "Could not reset password. Please try again.");
    }
    setIsLoading(false);
  };

  const onEmailChange = (newEmail) => {
    const trimLower = newEmail.trim().toLowerCase();
    const prevResolved = lastBlurredResolvedEmailRef.current;
    if (prevResolved != null && trimLower !== prevResolved) {
      lastBlurredResolvedEmailRef.current = null;
      updateMatchedCustomerDataForEmail("");
    }
    setEmail(newEmail);
    setEmailError("");
    setPasswordError("");
    setLocalPassword("");
    setShowLocalPassword(false);
    setSelectedIdpConfig(null);
    setError(null);
    setSuccessMessage(null);
  };

  const onEmailBlur = (value) => {
    const trimmed = value.trim();
    const valid = trimmed && LOGIN_EMAIL_REGEX.test(trimmed);
    updateMatchedCustomerDataForEmail(value);
    lastBlurredResolvedEmailRef.current = valid ? trimmed.toLowerCase() : null;
  };

  const isEmailDirty = email !== initialEmailRef.current;
  const usePlatformAdminFlowUi = isAdminAccess || matchedCustomerData?.type === "internal";
  const customerEnv = matchedCustomerData
    ? resolveCustomerEnv(fetchedCustomerData || matchedCustomerData)
    : "";
  const envConfigsForCurrentEnv = matchedCustomerData && !usePlatformAdminFlowUi
    ? getEnvEmailDomainConfigs(fetchedCustomerData || matchedCustomerData, customerEnv)
    : [];
  const distinctSignInOptions = resolveSignInOptionsForEmail(envConfigsForCurrentEnv, email);
  const singleLocalOnly =
    distinctSignInOptions.length === 1 && signInOptionUsesLocalPassword(distinctSignInOptions[0]);
  const singleFederatedOnly =
    distinctSignInOptions.length === 1 && !signInOptionUsesLocalPassword(distinctSignInOptions[0]);
  /** Multiple domain-matched IdPs: show picker. Single match uses password (local) or primary SSO button (federated). */
  const showSignInChooser =
    !usePlatformAdminFlowUi
    && matchedCustomerData
    && distinctSignInOptions.length > 1;
  const showPlatformLoginFields = Boolean(usePlatformAdminFlowUi && matchedCustomerData && !platformUseOkta);
  const showLocalPasswordBlock = Boolean(
    !usePlatformAdminFlowUi
    && matchedCustomerData
    && (
      singleLocalOnly
      || (distinctSignInOptions.length > 1 && signInOptionUsesLocalPassword(selectedIdpConfig))
    )
  );
  const validLoginEmailFormat = Boolean(String(email || "").trim() && LOGIN_EMAIL_REGEX.test(String(email || "").trim()));
  const noSignInMethodConfigured = Boolean(
    !usePlatformAdminFlowUi
    && fetchedCustomerData
    && validLoginEmailFormat
    && distinctSignInOptions.length === 0
  );
  const showPasswordFields = showPlatformLoginFields || showLocalPasswordBlock;

  const primaryButtonLabel =
    noSignInMethodConfigured
      ? "No sign-in method for this email"
      : showPasswordFields
        ? "Sign in"
        : singleFederatedOnly
          ? formatIdpLoginButtonLabel(distinctSignInOptions[0])
          : showSignInChooser
            ? "Select a sign-in method above"
            : matchedCustomerData?.idpDisplayName ||
              (matchedCustomerData ? `Proceed with ${matchedCustomerData.displayName || matchedCustomerData.name}` : "Enter a valid email address");

  const primaryButtonDisabled =
    isLoading
    || !matchedCustomerData
    || !isEmailDirty
    || noSignInMethodConfigured
    || (showSignInChooser && !showPasswordFields)
    || (showPasswordFields && !localPassword.trim());

  const showForgotPasswordLink = showLocalPasswordBlock && !forgotStep;
  const showAdminOktaSilentSplash = Boolean(isAdminAccess && platformUseOkta && adminSilentProbeActive);

  return {
    brand,
    isAdminAccess,
    showAdminOktaSilentSplash,
    forgotStep,
    error,
    successMessage,
    isLoading,
    email,
    emailError,
    localPassword,
    showLocalPassword,
    setLocalPassword,
    setShowLocalPassword,
    passwordError,
    forgotCode,
    setForgotCode,
    forgotNewPassword,
    setForgotNewPassword,
    forgotConfirmPassword,
    setForgotConfirmPassword,
    showForgotNewPassword,
    setShowForgotNewPassword,
    handleForgotRequestSubmit,
    handleForgotConfirmSubmit,
    handleForgotBack,
    onEmailChange,
    onEmailBlur,
    handleEmailSubmit,
    showSignInChooser,
    signInChooserNeedsHeading: distinctSignInOptions.length > 1,
    noSignInMethodConfigured,
    distinctSignInOptions,
    selectedIdpConfig,
    setSelectedIdpConfig,
    startCustomerSsoRedirect,
    showPasswordFields,
    showForgotPasswordLink,
    primaryButtonLabel,
    primaryButtonDisabled,
    usePlatformAdminFlow: usePlatformAdminFlowUi,
    platformUseOkta,
    handleForgotPasswordClick,
    setError,
    setPasswordError,
  };
}
